import {shallowEqual, useSelector} from 'react-redux';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import classnames from 'classnames';

import {MHToolTip} from '@/components/base';
import {outboundCall} from '@/services/awsConnectApiService';
import {videoProvider} from '@/services/videoService';
import {MissedCallSummary} from '@/types/observationsModels';

import {AGENT_STATUSES} from '@/constants/constants';
import {callsSelector, isVideoCallsEnabledSelector} from '../../redux/selectors';

import styles from './callActions.module.scss';
import {isDisplayMissedCallsLogEnabledSelector, phoneCallDataSelector} from "../../../redux/selectors";

type CallActionsProps = {
    patientId: number;
    patientFirstName: string;
    patientLastName: string;
    callType: MissedCallSummary['type'];
    hasActiveKitAssigned: boolean;
    latestPhoneNumber: string;
};

export const CallActions = ({
    patientId,
    patientFirstName,
    patientLastName,
    callType,
    hasActiveKitAssigned,
    latestPhoneNumber,
}: CallActionsProps) => {
    const {awsConnectAgentStatus, email} = useSelector(callsSelector, shallowEqual);
    const {isVideoCallsEnabled} = useSelector(isVideoCallsEnabledSelector);
    const {isDisplayMissedCallsLogEnabled} = useSelector(isDisplayMissedCallsLogEnabledSelector, shallowEqual);
    const {isDoNotDisturbedEnabled} = useSelector(phoneCallDataSelector, shallowEqual);

    const outboundCallsAllowed = (awsConnectAgentStatus === AGENT_STATUSES.OFFLINE && isDoNotDisturbedEnabled) ||
        awsConnectAgentStatus === AGENT_STATUSES.AVAILABLE;

    const videoCallDisabled = !hasActiveKitAssigned ||
        (isDisplayMissedCallsLogEnabled ? !outboundCallsAllowed : awsConnectAgentStatus === AGENT_STATUSES.OFFLINE);

    const audioCallDisabled = window.DISABLE_AWS_LOGIN === 'true' || !latestPhoneNumber ||
        (isDisplayMissedCallsLogEnabled ? !outboundCallsAllowed : awsConnectAgentStatus === AGENT_STATUSES.OFFLINE);

    const getVideoCallTooltip = () => {
        const callType = 'Video Call';
        if (!hasActiveKitAssigned) {
            return `${callType} (issue with phone number)`;
        }
        if (videoCallDisabled) {
            return `${callType} (unavailable in Offline mode)`;
        }
        return callType;
    };

    return (
        <>
            {isVideoCallsEnabled && (callType === 'VIDEO' || callType === 'PERS') ? (
                <MHToolTip tooltipContent={getVideoCallTooltip()} light>
                    <VideocamOutlinedIcon
                        onClick={async (event) => {
                            event.stopPropagation();
                            if (!videoCallDisabled) {
                                await videoProvider.createVideoMeeting(patientId);
                            }
                        }}
                        className={classnames(styles.callIcon, {
                            [styles.disabledCallIcon]: videoCallDisabled,
                        })}
                    />
                </MHToolTip>
            ) : (
                <MHToolTip
                    tooltipContent={`Phone Call ${audioCallDisabled ? '(unavailable in Offline mode)' : ''}`}
                    light
                >
                    <LocalPhoneOutlinedIcon
                        className={classnames(styles.callIcon, {
                            [styles.disabledCallIcon]: audioCallDisabled,
                        })}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (!audioCallDisabled) {
                                outboundCall(latestPhoneNumber, {
                                    mcrnEmail: email,
                                    patientId,
                                    patientFirstName,
                                    patientLastName,
                                });
                            }
                        }}
                    />
                </MHToolTip>
            )}
        </>
    );
};
